"use client";

import { Button } from "@package/subframe/components/Button";
import { FeatherPlus } from "@subframe/core";

/**
 * The Submit / Cancel footer shared by the per-unit check forms, plus the
 * "Submit and Raise Follow-up" shortcut (AC16).
 */
interface CheckSubmitFooterProps {
  /** Omitted where the form is inline on its log page and has nowhere to cancel back to. */
  onCancel?: () => void;
  onSubmit: () => void;
  /** Omitted where the check type has no follow-up shortcut; enabled only once something has failed. */
  onSubmitAndRaiseFollowUp?: () => void;
  /** Blocks all three actions while the submission is in flight. */
  isSubmitting: boolean;
  /** Blocks submission while the form is incomplete (AC13). */
  canSubmit: boolean;
  /** True when at least one unit has been marked as failing. */
  hasFailure: boolean;
  /** Shown on the left, e.g. "2 lights still to record". */
  hint?: string;
  submitLabel?: string;
}

export function CheckSubmitFooter({
  onCancel,
  onSubmit,
  onSubmitAndRaiseFollowUp,
  isSubmitting,
  canSubmit,
  hasFailure,
  hint,
  submitLabel = "Submit check",
}: CheckSubmitFooterProps) {
  return (
    <div className="flex w-full flex-wrap items-center justify-end gap-3">
      {hint && (
        <span className="mr-auto text-body font-body text-subtext-color">
          {hint}
        </span>
      )}
      {onCancel && (
        <Button
          type="button"
          variant="neutral-primary"
          size="large"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
      )}
      {onSubmitAndRaiseFollowUp && (
        <Button
          type="button"
          variant="neutral-secondary"
          size="large"
          icon={<FeatherPlus />}
          onClick={onSubmitAndRaiseFollowUp}
          disabled={isSubmitting || !canSubmit || !hasFailure}
        >
          Submit and Raise Follow-up
        </Button>
      )}
      <Button
        type="button"
        variant="brand-primary"
        size="large"
        onClick={onSubmit}
        disabled={isSubmitting || !canSubmit}
      >
        {isSubmitting ? "Submitting..." : submitLabel}
      </Button>
    </div>
  );
}
